import { API } from '@labrav/common'
import { FundingRequestsResponse } from '../types'

/**
 * Funding dashboard data access. Uses the shared authed axios client from
 * `@labrav/common` (its request interceptor attaches the Bearer token), so the
 * MFE calls the real endpoint itself — the host only passes the ids + base URL.
 */

export interface RecommendationGroupSample {
  opportunity_id: string | null
  customer_name: string | null
}

export interface RecommendationGroupSummary {
  group_id: string
  source: string
  program?: string | null
  program_name?: string | null
  eligibility_status?: string | null
  recommendation_type?: string | null
  recommendation?: string | null
  action?: string | null
  count: number
  total_claimable?: string | null
  sample: RecommendationGroupSample[]
}

export interface FundingProgramTotals {
  total_submitted: number
  approved: string
  approved_count: number
  pending: string
  pending_count: number
  currency: string
  potential: string | null
  potential_count: number | null
  recommendation_groups?: RecommendationGroupSummary[]
}

export interface CombinedFundingSummaryResponse {
  currency: string
  combined: FundingProgramTotals
}

export const getFundingSummary = (serviceUrl: string, accountId: string) =>
  API.get<CombinedFundingSummaryResponse>(
    `${serviceUrl}/v1/partners/${accountId}/funding_summary`
  )

/** One wallet allocation (e.g. an AWS MPOPP benefit) — amounts are USD decimal
 * strings, matching the service schema. */
export interface FundingWalletItem {
  labra_identifier: string
  program: string
  wallet_identifier: string
  wallet_name: string | null
  wallet_type: string
  status: string
  is_used: boolean
  allocated_amount: string
  consumed_amount: string
  remaining_amount: string
  currency: string
  expiry_date: string | null
  last_refreshed_at: string | null
  funding_needed_amount: string | null
}

export interface FundingWalletListResponse {
  wallets: FundingWalletItem[]
}

/** Wallet types shown in the dashboard sidebar. */
const SIDEBAR_WALLET_TYPES = 'CASH,CREDITS'

export const getFundingWallet = (serviceUrl: string, partnerId: string) =>
  API.get<FundingWalletListResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/wallet?wallet_type=${SIDEBAR_WALLET_TYPES}`
  )

/** Probe result for Partner Central Benefits API (IAM simulate + live ListBenefits). */
export type FundingBenefitsAccessReason =
  | 'ok'
  | 'feature_not_enabled'
  | 'iam_denied'
  | 'role_missing'
  | 'error'

export interface FundingBenefitsAccessResponse {
  benefits_enabled: boolean
  reason: FundingBenefitsAccessReason
  mpopp_grow_wallet_found: boolean
}

export const getFundingBenefitsAccess = (
  serviceUrl: string,
  partnerId: string,
  options?: { refresh?: boolean }
) =>
  API.get<FundingBenefitsAccessResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/benefits-access`,
    options?.refresh ? { params: { refresh: true } } : undefined
  )

/** One entry in the dashboard "Recent funding activities" sidebar feed. */
export interface FundingActivity {
  id: string
  workflow_id: string
  program: string
  company_name: string | null
  subject?: string | null
  funding_application_id: string | null
  display_status: string
  action_type: string
  event_at: string
  summary: string
  actor: string
}

export interface FundingActivitiesResponse {
  total: number
  activities: FundingActivity[]
}

export interface FundingActivitiesParams {
  program?: string
  workflow_id?: string
  limit?: number
  offset?: number
}

export const getFundingActivities = (
  serviceUrl: string,
  partnerId: string,
  params: FundingActivitiesParams = {}
) =>
  API.get<FundingActivitiesResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/activities`,
    { params }
  )

export interface FundingWalletHistoryChange {
  field: string
  previous_value: string | null
  current_value: string | null
}

export interface FundingWalletHistoryUpdate {
  id: string
  labra_identifier: string
  wallet_identifier: string
  program: string
  event_at: string
  event_type: string
  summary: string | null
  changes: FundingWalletHistoryChange[]
}

export interface FundingWalletHistoryResponse {
  total: number
  updates: FundingWalletHistoryUpdate[]
}

export const getFundingWalletHistory = (
  serviceUrl: string,
  partnerId: string,
  labraIdentifier: string,
  params: { limit?: number; offset?: number } = {}
) =>
  API.get<FundingWalletHistoryResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/wallet/${labraIdentifier}/history`,
    { params }
  )

export type FundingSearchField =
  | 'company_name'
  | 'opportunity_id'
  | 'crm_opportunity_id'
  | 'funding_application_id'

export interface FundingRequestsParams {
  limit?: number
  offset?: number
  sort?: string
  program?: string
  program_name?: string[]
  status?: string[]
  search?: string
  search_by?: FundingSearchField
  created_after?: string
  created_before?: string
  updated_after?: string
  updated_before?: string
  expiry_after?: string
  expiry_before?: string
  amount_min?: string
  amount_max?: string
  include_drafts?: boolean
}

/** The subset of request params driven by the filter panel + column sort. */
export type FundingRequestsQuery = Omit<
  FundingRequestsParams,
  'limit' | 'offset'
>

/**
 * Split the UI sort key (`<column>_<direction>`, e.g. `amount_desc`) into the
 * endpoint's two params: `sort_column` (last_activity | created | updated |
 * amount | expiry) and `sort` (asc | desc). Returns `{}` for an unset/malformed
 * key so no sort params are sent (endpoint applies its own default).
 */
const splitSort = (
  key: string | undefined
): { sort_column?: string; sort?: 'asc' | 'desc' } => {
  if (!key) return {}
  const idx = key.lastIndexOf('_')
  if (idx <= 0) return {}
  const sort_column = key.slice(0, idx)
  const direction = key.slice(idx + 1)
  if (direction !== 'asc' && direction !== 'desc') return {}
  return { sort_column, sort: direction }
}

export const getFundingRequests = (
  serviceUrl: string,
  partnerId: string,
  params: FundingRequestsParams = {}
) => {
  // The UI carries a single combined sort key; the endpoint wants it split into
  // `sort_column` + `sort` (direction), so drop the combined value and expand.
  const { sort, ...rest } = params
  const wireParams = { ...rest, ...splitSort(sort) }
  return API.get<FundingRequestsResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/requests`,
    // `indexes: null` repeats array params without brackets
    // (`status=A&status=B`), matching the endpoint's "repeat for multiple
    // values" contract; the axios default `status[]=A` would not bind.
    { params: wireParams, paramsSerializer: { indexes: null } }
  )
}

export type FundingStageState = 'completed' | 'current' | 'upcoming'

export interface FundingStage {
  stage: string
  entered_at: string | null
  state: FundingStageState
}

export interface FundingProgress {
  current_stage: string | null
  current_status: string | null
  stages: FundingStage[]
}

export interface FundingContact {
  job_title?: string | null
  email?: string | null
  first_name?: string | null
  last_name?: string | null
}

export interface FundingRunAttachment {
  doc_type: string
  filename?: string | null
  file_path: string
  s3_uri: string
  cloudfront_url?: string | null
  business_use_case?: string | null
  linked_at?: string | null
  linked_by?: string | null
}

export interface FundingDetailFields {
  program_category?: string | null
  funding_type?: string | null
  project_name?: string | null
  description?: string | null
  submission_trigger?: string | null
  confidence?: number | null
  acceptance_chance?: number | null
  contact?: FundingContact | null
  attachments?: FundingRunAttachment[] | null
}

export interface FundingRun {
  workflow_id: string
  status: string
  program: string
  opportunity_id: string | null
  crm_opportunity_id?: string | null
  funding_application_id: string | null
  company_name: string | null
  referral_id: string | null
  display_status: string | null
  fund_request_id: string | null
  funding_program_label: string | null
  claimed_amount: string | null
  approved_amount: string | null
  amount: string | null
  auto_submitted: boolean
  last_error: string | null
  funding_progress: FundingProgress | null
  detail_fields: FundingDetailFields | null
  last_result_json: {
    confidence?: number | null
    acceptance_chance?: number | null
  } | null
}

export interface FundingTimelineEvent {
  id: string
  action_type: string | null
  event_at: string
  actor: string | null
  summary: string | null
}

export interface FundingRunDetailsResponse {
  run: FundingRun
  timeline: {
    total: number
    events: FundingTimelineEvent[]
  }
}

export const getFundingRequestDetails = (
  serviceUrl: string,
  partnerId: string,
  program: string,
  workflowId: string,
  params: { limit?: number; offset?: number } = {}
) =>
  API.get<FundingRunDetailsResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/${encodeURIComponent(program)}/runs/${workflowId}/details`,
    { params: { limit: 50, offset: 0, ...params } }
  )

export interface FundingDocumentToAttach {
  s3_uri: string
  filename: string
  doc_type: string
  business_use_case?: string
}

/**
 * Link already-uploaded documents to a run. The response lists every document on
 * the run, but without the download URLs the detail response carries, so callers
 * refetch the details rather than rendering what comes back here.
 */
export const attachFundingDocuments = (
  serviceUrl: string,
  partnerId: string,
  program: string,
  workflowId: string,
  documents: FundingDocumentToAttach[]
) =>
  API.post<{ workflow_id: string }>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/${encodeURIComponent(program)}/runs/${workflowId}/documents`,
    { documents }
  )

/** One program card in the "Funding eligibility programs" tab. */
export interface FundingEligibilityProgram {
  program_name: string
  program_parent: string | null
  workflow_key: string | null
  description: string | null
  external_url: string | null
  prerequisite_status: string | null
  prerequisite_label: string | null
  potential_funding: string | null
  eligible_referrals_count: number
  has_evaluation: boolean
  requests_count: number
}

export interface FundingEligibilityProgramsResponse {
  evaluated_at: string | null
  programs: FundingEligibilityProgram[]
}

export const getFundingEligibilityPrograms = (
  serviceUrl: string,
  partnerId: string,
  params: { parent?: string } = {}
) =>
  API.get<FundingEligibilityProgramsResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/eligibility/programs`,
    { params }
  )

export interface FundingSettingsBillingAddress {
  address: string | null
  country: string | null
  state: string | null
  city: string | null
  postal_code: string | null
}

export interface FundingSettingsAutomation {
  program: string
  program_label: string
  auto_submit: boolean
  enabled: boolean
  auto_submission_limit_usd?: string | null
  auto_submission_utilized_usd?: string | null
  /** Copy for the program's info icon; no tooltip when absent. */
  description?: string | null
  /** Curated Partner Central program eligibility (funding_benefit_types). */
  eligibility_criteria?: string | null
  /**
   * What has to be true before this program auto-submits. Newline-separated:
   * the first line is the condition, any following line is an aside (e.g. the
   * auto-submit limit) and is not sent for every program.
   */
  auto_submit_criteria?: string | null
}

/** One settings-change audit entry (who toggled what, when). */
export interface FundingSettingsAuditEntry {
  id: string
  action_type: string
  summary: string
  actor: string | null
  event_at: string
}

export interface FundingSettings {
  payee_id: string | null
  billing_address: FundingSettingsBillingAddress | null
  automation: FundingSettingsAutomation[]
  audit_log?: FundingSettingsAuditEntry[]
  // TODO(funding): confirm backend field names — info-icon tooltip copy is
  // server-provided; icons render a tooltip only when their field is present.
  payee_tooltip?: string | null
  auto_submit_tooltip?: string | null
}

// `updated_by` is the actor display label the backend records against the audit
// entry (falls back to the JWT uid when omitted); `updated_by_labra_user_id` is
// the Labra user id, sent so the backend can store it alongside the name.
export type FundingSettingsPatch = Partial<FundingSettings> & {
  updated_by?: string
  updated_by_labra_user_id?: string
}

export const getFundingSettings = (serviceUrl: string, partnerId: string) =>
  API.get<FundingSettings>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/settings`
  )

export const patchFundingSettings = (
  serviceUrl: string,
  partnerId: string,
  body: FundingSettingsPatch
) =>
  API.patch<FundingSettings>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/settings`,
    body
  )

/** Catalog rows from GET /v1/partners/funding/types. */
export interface FundingBenefitTypeItem {
  id: string
  program_parent: string | null
  program: string
  fulfillment_types: string[]
  cash_claim: boolean
  create_template: boolean
  workflow_key: string | null
  status: string | null
  description?: string | null
  eligibility_criteria?: string | null
}

export interface FundingBenefitTypesResponse {
  catalog: string
  types: FundingBenefitTypeItem[]
  parents: string[]
}

export type GetFundingBenefitTypesParams = {
  /** When true, only create_template rows (+ API-side MPOPP filter if any). */
  templateEligible?: boolean
  workflowOnly?: boolean
}

/** GET /v1/partners/funding/types — synced benefit catalog (DB, with registry fallback). */
export const getFundingBenefitTypes = (
  serviceUrl: string,
  params: GetFundingBenefitTypesParams = {}
) =>
  API.get<FundingBenefitTypesResponse>(
    `${serviceUrl}/v1/partners/funding/types`,
    {
      params: {
        ...(params.templateEligible ? { template_eligible: true } : {}),
        ...(params.workflowOnly ? { workflow_only: true } : {}),
      },
    }
  )

/** Settings matrix: Funding Template rows + MPOPP Grow/Activate. */
export const getFundingTemplateEligibleTypes = (serviceUrl: string) =>
  getFundingBenefitTypes(serviceUrl, { templateEligible: true })

export type FundingTemplateStatus = 'active' | 'disabled' | 'archived'
export type FundingTemplateFieldOverrideMode = 'inherit' | 'override'
export type FundingTemplateDefaultsMode = 'manual_required' | 'inherit_globals'

export interface FundingTemplateFieldOverride {
  mode: FundingTemplateFieldOverrideMode
  value: string | null
}

export interface FundingTemplateResourceDocument {
  s3_uri: string
  doc_type: string
  filename?: string | null
  /** Partner Central FileDetails BusinessUseCase. */
  business_use_case?: string | null
}

export interface FundingTemplateFileTypeOption {
  business_use_case: string
  doc_type: string
  label: string
}

export interface FundingTemplateResources {
  sow?: FundingTemplateResourceDocument | null
  pricing_calculator_url?: string | null
  pricing_calculator_resources?: FundingCalculatorResource[] | null
  pricing_calculator_scaling?: FundingCalculatorScaling[] | null
  documents?: FundingTemplateResourceDocument[] | null
}

export interface FundingCalculatorScaling {
  resource_id: string
  mode: 'scaled' | 'fixed'
  scale_fields?: string[]
}

export interface FundingCalculatorResourceField {
  field_id: string
  label: string
  kind: string
  current_value: string | null
  unit: string | null
  scalable: boolean
}

export interface FundingCalculatorResource {
  resource_id: string
  service_code: string
  service_name: string
  region: string | null
  upfront_cost_usd: number
  monthly_cost_usd: number
  config_summary: string | null
  fields: FundingCalculatorResourceField[]
}

export interface FundingCalculatorInspectResponse {
  estimate_id: string
  monthly_total_usd: number
  arr_usd: number
  resources: FundingCalculatorResource[]
}

export interface PocCalculatorEdit {
  resource_id: string
  values: Record<string, string>
  monthly_usd: string
}

export interface PocCalculatorPlanResource {
  resource_id: string
  service_name: string
  status: 'scaled' | 'edited' | 'fixed' | 'fixed_pricing_failed'
  fields: Record<string, { old: string; new: string }>
  new_monthly_usd: number
  new_upfront_usd: number
}

export interface PocCalculatorPreviewResponse {
  preview_token: string
  resources: FundingCalculatorResource[]
  plan: {
    projected_monthly_usd: number
    resources: PocCalculatorPlanResource[]
  }
}

export interface FundingTemplateSubFundingType {
  benefit_identifier: string
  program_name: string
  funding_type?: string | null
  program_parent?: string | null
}

export interface FundingTemplateListItem {
  template_id: string
  name: string | null
  description: string | null
  sub_funding_type: FundingTemplateSubFundingType
  resources_summary: string
  resources_configured_count: number
  status: FundingTemplateStatus
  created_by?: string | null
  created_at: string
  updated_by?: string | null
  updated_at?: string | null
  /** Funding requests created with this template. */
  funding_request_count?: number
}

export interface FundingTemplateListResponse {
  total: number
  templates: FundingTemplateListItem[]
}

export interface FundingTemplateDetail {
  template_id: string
  name: string | null
  description: string | null
  sub_funding_type: FundingTemplateSubFundingType
  status: FundingTemplateStatus
  force_aws_account_id: boolean
  resources: FundingTemplateResources | null
  resources_summary: string
  resources_configured_count: number
  defaults_mode: FundingTemplateDefaultsMode
  global_defaults: Record<string, string | null> | null
  field_overrides: Record<string, FundingTemplateFieldOverride> | null
  resolved_fields: Record<string, string | null>
  created_by?: string | null
  updated_by?: string | null
  created_at: string
  updated_at: string
  /** Funding requests created with this template. */
  funding_request_count?: number
}

export interface FundingTemplateActorBody {
  updated_by?: string | null
  updated_by_labra_user_id?: string | null
}

export interface FundingTemplateCreateBody extends FundingTemplateActorBody {
  benefit_identifier: string
  name?: string | null
  description?: string | null
  field_overrides?: Record<string, FundingTemplateFieldOverride> | null
  force_aws_account_id?: boolean | null
  resources?: FundingTemplateResources | null
  status?: 'active' | 'disabled' | null
}

export type FundingTemplatePatchBody = Partial<
  Omit<FundingTemplateCreateBody, 'benefit_identifier'>
> &
  FundingTemplateActorBody & {
    name?: string | null
    description?: string | null
  }

export interface FundingTemplateActivityEntry {
  id: string
  action_type: string
  summary: string
  actor?: string | null
  actor_labra_user_id?: string | null
  event_at: string
}

export interface FundingTemplateActivityResponse {
  entries: FundingTemplateActivityEntry[]
}

export interface FundingTemplateUsageEntry {
  workflow_id: string
  program_label: string
  used_at: string
  company_name?: string | null
  opportunity_id?: string | null
  program?: string | null
  funding_request_title?: string | null
  status?: string | null
  created_by?: string | null
}

export interface FundingTemplateUsagesResponse {
  entries: FundingTemplateUsageEntry[]
}

export interface FundingDefaultFieldValuesResponse {
  configured: boolean
  values: Record<string, string | null> | null
  summary: Record<string, string | null> | null
  /** Enum options for Basic information dropdowns (ExecutedCountry / ExecutedState). */
  fields?: FundingTemplateFormField[] | null
  /** Settings Audit logs rail (same store as GET /funding/settings). */
  audit_log?: FundingSettingsAuditEntry[] | null
}

export interface FundingTemplateFormField {
  path: string
  label: string
  type: string
  required: boolean
  group?: string | null
  enum?: string[] | null
  /** Display labels keyed by enum value (e.g. US → United States). */
  option_labels?: Record<string, string> | null
  max_length?: number | null
  /** Schema help text for tooltips. */
  description?: string | null
  /** Constant-across-apps default from catalog static_field_defaults. */
  default?: string | null
}

export interface FundingTemplateFormSchemaResponse {
  benefit_identifier: string
  program_name: string
  /** Static-across-apps subset shown by default (with scan defaults). */
  fields: FundingTemplateFormField[]
  /** Full GetBenefit flatten for Add-field picker. */
  available_fields?: FundingTemplateFormField[]
  resources: {
    files_required: boolean
    /** True when FileDetails exists even if optional. */
    files_supported?: boolean
    supports_pricing_calculator: boolean
    /** From FileDetails title when AWS provides it. */
    file_label?: string | null
    /** From FileDetails description for upload tooltips. */
    file_description?: string | null
    /** Full AWS BusinessUseCase list for typed optional attach. */
    file_types?: FundingTemplateFileTypeOption[] | null
  }
}

export interface FundingTemplateRuleSuggestion {
  label: string
  text: string
}

export const listFundingTemplates = (
  serviceUrl: string,
  partnerId: string,
  params: {
    include_archived?: boolean
    benefit_identifier?: string
    status?: FundingTemplateStatus[]
  } = {}
) =>
  API.get<FundingTemplateListResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates`,
    { params }
  )

export const getFundingTemplate = (
  serviceUrl: string,
  partnerId: string,
  templateId: string
) =>
  API.get<FundingTemplateDetail>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/${templateId}`
  )

export const createFundingTemplate = (
  serviceUrl: string,
  partnerId: string,
  body: FundingTemplateCreateBody
) =>
  API.post<FundingTemplateDetail>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates`,
    body
  )

export const patchFundingTemplate = (
  serviceUrl: string,
  partnerId: string,
  templateId: string,
  body: FundingTemplatePatchBody
) =>
  API.patch<FundingTemplateDetail>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/${templateId}`,
    body
  )

export const archiveFundingTemplate = (
  serviceUrl: string,
  partnerId: string,
  templateId: string,
  body: FundingTemplateActorBody = {}
) =>
  API.post<FundingTemplateDetail>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/${templateId}/archive`,
    body
  )

export const disableFundingTemplate = (
  serviceUrl: string,
  partnerId: string,
  templateId: string,
  body: FundingTemplateActorBody = {}
) =>
  API.post<FundingTemplateDetail>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/${templateId}/disable`,
    body
  )

export const enableFundingTemplate = (
  serviceUrl: string,
  partnerId: string,
  templateId: string,
  body: FundingTemplateActorBody = {}
) =>
  API.post<FundingTemplateDetail>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/${templateId}/enable`,
    body
  )

export const cloneFundingTemplate = (
  serviceUrl: string,
  partnerId: string,
  templateId: string,
  body: FundingTemplateActorBody = {}
) =>
  API.post<FundingTemplateDetail>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/${templateId}/clone`,
    body
  )

export const getFundingTemplateActivity = (
  serviceUrl: string,
  partnerId: string,
  templateId: string
) =>
  API.get<FundingTemplateActivityResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/${templateId}/activity`
  )

export const listFundingTemplateUsages = (
  serviceUrl: string,
  partnerId: string,
  templateId: string
) =>
  API.get<FundingTemplateUsagesResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/${templateId}/usages`
  )

export const getFundingDefaultFieldValues = (
  serviceUrl: string,
  partnerId: string
) =>
  API.get<FundingDefaultFieldValuesResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/default-field-values`
  )

export const putFundingDefaultFieldValues = (
  serviceUrl: string,
  partnerId: string,
  values: Record<string, string | null>,
  options?: {
    benefitIdentifier?: string
    updatedBy?: string
    updatedByLabraUserId?: string
  }
) =>
  API.put<FundingDefaultFieldValuesResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/default-field-values`,
    {
      values,
      ...(options?.updatedBy ? { updated_by: options.updatedBy } : {}),
      ...(options?.updatedByLabraUserId
        ? { updated_by_labra_user_id: options.updatedByLabraUserId }
        : {}),
    },
    options?.benefitIdentifier
      ? { params: { benefit_identifier: options.benefitIdentifier } }
      : undefined
  )

export const getFundingTemplateFormSchema = (
  serviceUrl: string,
  partnerId: string,
  benefitIdentifier: string
) =>
  API.get<FundingTemplateFormSchemaResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/form-schema`,
    { params: { benefit_identifier: benefitIdentifier } }
  )

export const suggestFundingTemplateName = (
  serviceUrl: string,
  partnerId: string,
  body: { benefit_identifier: string; description?: string | null }
) =>
  API.post<{ name: string }>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/suggest-name`,
    body
  )

// TODO(funding): confirm against chat-service FUNDING_SETTINGS_TEMPLATES_API.md once inspect ships.
export const inspectFundingTemplateCalculator = (
  serviceUrl: string,
  partnerId: string,
  shareUrl: string
) =>
  API.post<FundingCalculatorInspectResponse>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/calculator/inspect`,
    { share_url: shareUrl }
  )

export const previewPocCalculator = (
  serviceUrl: string,
  accountId: string,
  body: {
    opportunity_id: string
    template_id: string
    edits: PocCalculatorEdit[]
  }
) =>
  API.post<PocCalculatorPreviewResponse>(
    `${serviceUrl}/v1/partners/${accountId}/funding/poc/calculator/preview`,
    body
  )

export type GetFundingTemplateRuleSuggestionsParams = {
  benefitIdentifier: string
  /** Current AI-input draft — helps tailor chips. */
  description?: string | null
  /** Ask Bedrock for a fresh variety of chips. */
  regenerate?: boolean
}

export const getFundingTemplateRuleSuggestions = (
  serviceUrl: string,
  partnerId: string,
  benefitIdentifierOrParams: string | GetFundingTemplateRuleSuggestionsParams
) => {
  const params: GetFundingTemplateRuleSuggestionsParams =
    typeof benefitIdentifierOrParams === 'string'
      ? { benefitIdentifier: benefitIdentifierOrParams }
      : benefitIdentifierOrParams
  return API.get<{ suggestions: FundingTemplateRuleSuggestion[] }>(
    `${serviceUrl}/v1/partners/${partnerId}/funding/templates/rule-suggestions`,
    {
      params: {
        benefit_identifier: params.benefitIdentifier,
        ...(params.description?.trim()
          ? { description: params.description.trim() }
          : {}),
        ...(params.regenerate ? { regenerate: true } : {}),
      },
    }
  )
}
